import React, { createContext, useContext, useState, useEffect } from 'react';
// 临时替代 cn 函数
function cn(...classes: (string | boolean | undefined)[]) {
  return classes.filter(Boolean).join(' ');
}
import { 
  CheckCircle, XCircle, AlertCircle, Info, X, 
  Copy, Trash2, Send, User, Bot 
} from 'lucide-react';

// Toast 类型
export type ToastType = 'success' | 'error' | 'warning' | 'info' | 'action';

// Toast 数据接口
export interface ToastData {
  id?: string;
  title?: string;
  message: string;
  type: ToastType;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
  icon?: React.ReactNode;
  dismissible?: boolean;
}

// Toast 上下文
interface ToastContextType {
  toasts: ToastData[];
  addToast: (toast: Omit<ToastData, 'id'>) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

// 钩子函数
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

// Toast 提供者
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const addToast = (toast: Omit<ToastData, 'id'>) => {
    const id = Math.random().toString(36).substring(2, 9);
    const newToast: ToastData = {
      id,
      duration: 4000,
      dismissible: true,
      ...toast,
    };
    
    setToasts(prev => [...prev, newToast]);

    // 自动移除
    if (newToast.duration && newToast.duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, newToast.duration);
    }
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const clearToasts = () => {
    setToasts([]);
  };

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast, clearToasts }}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  );
}

// Toast 容器
function ToastContainer() {
  const { toasts, removeToast } = useToast();

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 pointer-events-none">
      {toasts.filter(t => t.id).map(toast => (
        <ToastItem key={toast.id} toast={toast} onDismiss={() => removeToast(toast.id!)} />
      ))}
    </div>
  );
}

// 单个 Toast 组件
function ToastItem({ toast, onDismiss }: { toast: ToastData; onDismiss: () => void }) {
  const [isLeaving, setIsLeaving] = useState(false);

  const handleDismiss = () => {
    setIsLeaving(true);
    setTimeout(() => {
      onDismiss();
    }, 300);
  };

  // 自动关闭效果
  useEffect(() => {
    if (toast.duration && toast.duration > 0) {
      const timer = setTimeout(() => {
        handleDismiss();
      }, toast.duration);

      return () => clearTimeout(timer);
    }
  }, [toast.duration]);

  // 图标映射
  const iconMap = {
    success: <CheckCircle className="w-5 h-5 text-green-500" />,
    error: <XCircle className="w-5 h-5 text-red-500" />,
    warning: <AlertCircle className="w-5 h-5 text-yellow-500" />,
    info: <Info className="w-5 h-5 text-blue-500" />,
    action: toast.icon || <Info className="w-5 h-5 text-blue-500" />,
  };

  // 背景颜色
  const bgColorMap = {
    success: 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800',
    error: 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800',
    warning: 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800',
    info: 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800',
    action: 'bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-700',
  };

  return (
    <div
      className={cn(
        "max-w-sm w-full pointer-events-auto",
        "transform transition-all duration-300 ease-out",
        isLeaving ? "opacity-0 translate-x-full" : "opacity-100 translate-x-0"
      )}
    >
      <div className={cn(
        "rounded-lg border shadow-lg p-4",
        bgColorMap[toast.type]
      )}>
        <div className="flex items-start gap-3">
          {/* 图标 */}
          <div className="flex-shrink-0 mt-0.5">
            {iconMap[toast.type]}
          </div>

          {/* 内容 */}
          <div className="flex-1 min-w-0">
            {toast.title && (
              <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm mb-0.5">
                {toast.title}
              </h4>
            )}
            <p className="text-gray-700 dark:text-gray-300 text-sm">
              {toast.message}
            </p>

            {/* 操作按钮 */}
            {toast.action && (
              <button
                onClick={() => {
                  toast.action?.onClick();
                  handleDismiss();
                }}
                className={cn(
                  "mt-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
                  toast.type === 'success' && "bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-800/30 dark:text-green-300 dark:hover:bg-green-800/50",
                  toast.type === 'error' && "bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-800/30 dark:text-red-300 dark:hover:bg-red-800/50",
                  toast.type === 'warning' && "bg-yellow-100 text-yellow-700 hover:bg-yellow-200 dark:bg-yellow-800/30 dark:text-yellow-300 dark:hover:bg-yellow-800/50",
                  toast.type === 'info' && "bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-800/30 dark:text-blue-300 dark:hover:bg-blue-800/50",
                  toast.type === 'action' && "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                )}
              >
                {toast.action.label}
              </button>
            )}
          </div>

          {/* 关闭按钮 */}
          {toast.dismissible && (
            <button
              onClick={handleDismiss}
              className="flex-shrink-0 ml-2 p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="关闭通知"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* 进度条（可选） */}
        {toast.duration && toast.duration > 0 && (
          <div className="mt-3 h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className={cn(
                "h-full transition-all duration-300 ease-linear",
                toast.type === 'success' && "bg-green-500",
                toast.type === 'error' && "bg-red-500",
                toast.type === 'warning' && "bg-yellow-500",
                toast.type === 'info' && "bg-blue-500",
                toast.type === 'action' && "bg-gray-500"
              )}
              style={{ 
                width: isLeaving ? '0%' : '100%',
                transition: `width ${toast.duration}ms linear`
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// 预定义的 Toast 快捷函数（仅用于组件内部，内部通过 addToast 直接操作）
function _createToastHelpers(addToast: (data: ToastData) => void) {
  return {
    success: (message: string, title?: string, options?: Partial<Omit<ToastData, 'id' | 'type' | 'message'>>) => {
      addToast({ title, message, type: 'success', duration: 3000, ...options });
    },
    error: (message: string, title?: string, options?: Partial<Omit<ToastData, 'id' | 'type' | 'message'>>) => {
      addToast({ title, message, type: 'error', duration: 5000, ...options });
    },
    warning: (message: string, title?: string, options?: Partial<Omit<ToastData, 'id' | 'type' | 'message'>>) => {
      addToast({ title, message, type: 'warning', duration: 4000, ...options });
    },
    info: (message: string, title?: string, options?: Partial<Omit<ToastData, 'id' | 'type' | 'message'>>) => {
      addToast({ title, message, type: 'info', duration: 3000, ...options });
    },
    action: (message: string, title?: string, options?: Partial<Omit<ToastData, 'id' | 'type' | 'message'>>) => {
      addToast({ title, message, type: 'action', duration: 5000, ...options });
    },
  };
}

// 特定的 Toast 快捷方式 — 改为 Hook，只能在组件内调用
export function useChatToast() {
  const context = useContext(ToastContext);
  const addToast = context?.addToast;
  const helpers = addToast ? _createToastHelpers(addToast) : null;
  return {
    messageSent: () => helpers?.success('消息已发送', '发送成功'),
    messageCopied: () => helpers?.success('消息已复制到剪贴板', '复制成功'),
    messageDeleted: () => helpers?.info('消息已删除', '删除成功'),
    sessionCreated: () => helpers?.success('新会话已创建', '创建成功'),
    sessionDeleted: () => helpers?.info('会话已删除', '删除成功'),
    aiThinking: () => helpers?.info('AI正在思考中...', '正在处理', { duration: 2000, dismissible: false }),
    aiResponseReady: () => helpers?.success('AI已回复', '回复就绪', { duration: 1500 }),
    connectionError: () => helpers?.error('网络连接失败，请检查网络设置', '连接失败'),
    rateLimit: () => helpers?.warning('请求过于频繁，请稍后再试', '频率限制'),
    studentInfoUpdated: () => helpers?.success('学生信息已更新', '更新成功'),
    studentInfoIncomplete: () => helpers?.warning('请补充完整学生信息以获得更准确的推荐', '信息不完整'),
  };
}

// ⚠️ 保留向后兼容：旧版 chatToast 对象已废弃，请使用 useChatToast() Hook
// 此对象保留用于非组件环境（如工具函数），但不会实际显示 toast
export const chatToast = (() => {
  const noop = () => {};
  return {
    messageSent: noop,
    messageCopied: noop,
    messageDeleted: noop,
    sessionCreated: noop,
    sessionDeleted: noop,
    aiThinking: noop,
    aiResponseReady: noop,
    connectionError: noop,
    rateLimit: noop,
    studentInfoUpdated: noop,
    studentInfoIncomplete: noop,
  };
})();

// 确认对话框快捷方式
export const confirmDialog = {
  deleteSession: (onConfirm: () => void) => {
    // 这里可以集成一个确认对话框组件
    const confirmed = window.confirm('确定要删除这个会话吗？此操作不可撤销。');
    if (confirmed) {
      onConfirm();
      chatToast.sessionDeleted();
    }
  },
  
  clearChat: (onConfirm: () => void) => {
    const confirmed = window.confirm('确定要清空当前对话吗？所有消息将被删除。');
    if (confirmed) {
      onConfirm();
      chatToast.messageDeleted();
    }
  },
  
  deleteMessage: (onConfirm: () => void) => {
    const confirmed = window.confirm('确定要删除这条消息吗？');
    if (confirmed) {
      onConfirm();
      chatToast.messageDeleted();
    }
  },
};